import {
  MutableRefObject,
  ReactNode,
  useEffect,
  useRef,
  useState,
} from "react";

import { useLabelBoundingContainerRef } from "../providers/LabelBoundingContainerProvider";
import { ArrowDownBorder, HoverLabel, PlayerControlButton } from "../styles";

export type TPlayerControlLabelButton = {
  interactive: boolean;
  Icon?: ReactNode;
  label?: string;
  hoverText: string;
  buttonRef?: MutableRefObject<null | HTMLButtonElement>;
  forceBottom?: boolean;
  forceLeft?: boolean;
} & React.HTMLAttributes<HTMLButtonElement>;

export const PlayerControlButtonComponent = ({
  Icon,
  buttonRef,
  hoverText,
  label,
  forceLeft,
  forceBottom,
  interactive,
  ...props
}: TPlayerControlLabelButton) => {
  const containerRef = useLabelBoundingContainerRef();
  const labelRef = useRef<HTMLDivElement>(null);
  const localButtonRef = useRef<HTMLButtonElement>(null);

  const [labelBelow, setLabelBelow] = useState(false);
  const [labelPosition, setLabelPosition] = useState<
    "left" | "right" | "center"
  >("center");

  useEffect(() => {
    if (forceLeft && forceBottom) {
      setLabelPosition("left");
      setLabelBelow(true);
      return;
    }

    if (!containerRef) return;

    const container = containerRef.current;
    const hoverLabel = labelRef.current;
    const buttonEl = buttonRef?.current ?? localButtonRef.current;

    if (container && hoverLabel && buttonEl) {
      const containerRect = container.getBoundingClientRect();
      const labelRect = hoverLabel.getBoundingClientRect();

      const labelLeft = labelRect.left < containerRect.left;
      const labelRight = labelRect.right > containerRect.right;

      setLabelBelow(labelRect.top < containerRect.top);

      if (labelLeft) {
        setLabelPosition("left");
      } else if (labelRight) {
        setLabelPosition("right");
      }
    }
  }, [containerRef, hoverText, buttonRef, forceLeft, forceBottom]);

  return (
    <PlayerControlButton
      ref={buttonRef ?? localButtonRef}
      tabIndex={interactive ? 0 : -1}
      aria-hidden={!interactive}
      {...props}
    >
      {Icon}
      {label && <span>{label}</span>}
      <HoverLabel ref={labelRef} $below={labelBelow} $position={labelPosition}>
        {hoverText}
        <ArrowDownBorder $position={labelPosition} $below={labelBelow} />
      </HoverLabel>
    </PlayerControlButton>
  );
};
